// ============================================================================
// PatientAccessTab — «Доступ и приглашения» кабинета пациента.
//
// Показывает ЧЕСТНУЮ картину доступа (SSoT — сервер):
//   • с какой учётной записью и карточками связан пациент (psy_clients /
//     medic_patients, по привязке user_id либо контактам);
//   • какие разделы кабинета доступны (чтение), а какие закрыты правами роли
//     «Пациент»; где нужно право — подсказка обратиться к администратору;
//   • приглашения на тестирование — выданные специалистом ссылки и короткие
//     коды (если назначения доступны для чтения).
//
// Изменение прав — только администратор базы; кабинет ничего не настраивает.
// ============================================================================

import { useEffect, useState } from 'react';
import { ExternalLink, KeyRound, Lock, ShieldCheck, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/CopyButton';
import { InlineNotice } from '@/components/medic/PatientCardStates';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { openPollCodeEntry } from '@/lib/pollGuestLinks';
import { publicBaseUrl } from '@/lib/publicBaseUrl';
import { pollLink } from '@/lib/psyWorkspace';
import { useToastStore } from '@/store/toastStore';
import type {
    CabinetAccessRow,
    CabinetIdentityState,
    PatientCabinetDashboard,
} from '@/lib/patientCabinetDashboard';
import { formatValue } from '@/lib/format';

interface Props {
    data: PatientCabinetDashboard;
    onRefresh: () => void;
}

function openExternalUrl(url: string): void {
    if (!url) return;
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) window.location.assign(url);
}

const STATE_LABELS: Record<CabinetIdentityState, string> = {
    ready: 'Доступно',
    denied: 'Нет доступа',
    unavailable: 'Не установлено',
    unlinked: 'Не связано',
    ambiguous: 'Нужно уточнить',
    error: 'Ошибка',
};

function stateClass(state: CabinetIdentityState): string {
    if (state === 'ready') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    if (state === 'denied' || state === 'ambiguous') return 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400';
    if (state === 'error') return 'border-destructive/40 bg-destructive/10 text-destructive';
    return 'border-border bg-muted/50 text-muted-foreground';
}

function StateBadge({ state }: { state: CabinetIdentityState }) {
    return (
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${stateClass(state)}`}>
            {STATE_LABELS[state]}
        </span>
    );
}

function AccessRows({ rows, onRetry }: { rows: CabinetAccessRow[]; onRetry: () => void }) {
    const hasError = rows.some((row) => row.state === 'error');
    return (
        <div className="space-y-2">
            {hasError && (
                <InlineNotice
                    text="Часть разделов не удалось загрузить — проверьте связь и повторите."
                    tone="danger"
                    onRetry={onRetry}
                />
            )}
            <div className="overflow-hidden rounded-lg border bg-card">
                <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-medium">Доступ к разделам</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">права роли «Пациент»</span>
                </div>
                {rows.map((row) => (
                    <div key={row.key} className="flex items-start gap-2 border-b px-3 py-2 last:border-0">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium">{row.title}</span>
                                <StateBadge state={row.state} />
                            </div>
                            <div className="mt-0.5 break-words text-[11px] text-muted-foreground">{row.detail}</div>
                        </div>
                        {row.state !== 'ready' && (
                            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function IdentityCard({ data }: { data: PatientCabinetDashboard }) {
    const { identity } = data;
    const matchLabel = (match: string): string => {
        if (match === 'account') return 'привязка к учётной записи';
        if (match === 'phone') return 'совпадение по телефону';
        if (match === 'email') return 'совпадение по почте';
        return 'связь не установлена';
    };
    return (
        <div className="rounded-lg border bg-card px-3 py-2">
            <div className="flex items-center gap-2">
                <UserRound className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Учётная запись пациента</span>
                <span className="ml-auto text-[10px] text-muted-foreground">
                    {identity.patientRole
                        ? 'роль «Пациент»'
                        : identity.isAdmin ? 'администратор' : 'роль не назначена'}
                </span>
            </div>
            <div className="mt-2 grid gap-1.5 text-xs sm:grid-cols-2">
                <div>
                    <span className="text-muted-foreground">Пользователь: </span>
                    {identity.name || identity.login || '—'}
                </div>
                <div>
                    <span className="text-muted-foreground">Логин: </span>
                    {identity.login || '—'}
                </div>
                <div>
                    <span className="text-muted-foreground">Телефон: </span>
                    {identity.phone || '—'}
                </div>
                <div>
                    <span className="text-muted-foreground">Почта: </span>
                    {identity.email || '—'}
                </div>
                <div className="sm:col-span-2">
                    <span className="text-muted-foreground">Карточка клиента: </span>
                    {data.identity.psy
                        ? `${data.identity.psy.name || 'без имени'} (${matchLabel(data.identity.psy.matchedBy)})`
                        : data.identity.psyNote || 'не связана'}
                </div>
                <div className="sm:col-span-2">
                    <span className="text-muted-foreground">Карточка пациента: </span>
                    {data.identity.medic
                        ? `${data.identity.medic.name || 'без имени'} (${matchLabel(data.identity.medic.matchedBy)})`
                        : data.identity.medicNote || 'не найдена'}
                </div>
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">
                Связь карточки и права разделов настраивает специалист/администратор базы.
                {identity.isAdmin && ' Вы администратор — доступны все разделы.'}
            </div>
        </div>
    );
}

function Invitations({ data, onRefresh }: { data: PatientCabinetDashboard; onRefresh: () => void }) {
    const { tests, invitations } = data;
    const showError = useToastStore((s) => s.showError);
    const [baseUrl, setBaseUrl] = useState('');
    const needBaseUrl = tests.kind === 'psy'
        && tests.assignmentsState === 'ready'
        && invitations.length > 0;

    // Внешний адрес базы (base_url публикации → внешний URL платформы/origin)
    // резолвится ОДИН раз и только когда есть что открывать: на desktop origin
    // (wails.localhost) недоступен телефону.
    useEffect(() => {
        if (!needBaseUrl) return;
        let cancelled = false;
        void publicBaseUrl()
            .then((value) => { if (!cancelled) setBaseUrl(value); })
            .catch((e) => {
                if (cancelled) return;
                logErrorToJournal(e, 'Кабинет пациента: адрес ссылки на тест');
                showError(friendlyError(e));
            });
        return () => { cancelled = true; };
    }, [needBaseUrl, showError]);

    const openCodeEntry = () => {
        void openPollCodeEntry().catch((e) => {
            logErrorToJournal(e, 'Кабинет пациента: страница ввода кода теста');
            showError(friendlyError(e));
        });
    };

    if (tests.kind !== 'psy') {
        return (
            <div className="rounded-lg border bg-card px-3 py-2 text-xs">
                <div className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                    <KeyRound className="h-3.5 w-3.5 text-primary" />
                    Приглашения на тестирование
                </div>
                <div className="text-muted-foreground">
                    {tests.kind === 'poll'
                        ? 'Доступные тесты и результаты — во вкладке «Тесты». Ссылки и коды на отдельные прохождения выдаёт специалист.'
                        : tests.note || 'Приглашения на тестирование выдаёт специалист.'}
                </div>
            </div>
        );
    }

    if (tests.assignmentsState !== 'ready') {
        const denied = tests.assignmentsState === 'denied';
        const failed = tests.assignmentsState === 'error';
        return (
            <div className="space-y-2 rounded-lg border bg-card px-3 py-2 text-xs">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                    <KeyRound className="h-3.5 w-3.5 text-primary" />
                    Приглашения на тестирование
                </div>
                <div className={failed ? 'text-destructive' : 'text-muted-foreground'}>
                    {denied
                        ? 'Список приглашений роли «Пациент» недоступен. Ссылку и код на тест передаёт специалист лично или в канале связи.'
                        : failed
                            ? (tests.assignmentsError || 'Не удалось загрузить назначения — ссылки и коды могут быть не видны.')
                            : 'Назначения методик недоступны — приглашения выдаёт специалист.'}
                </div>
                {failed && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px]"
                        onClick={onRefresh}
                    >
                        Повторить
                    </Button>
                )}
            </div>
        );
    }

    if (invitations.length === 0) {
        return (
            <div className="rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                Приглашений нет. Специалист назначит методику — здесь появятся ссылка и короткий код.
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-lg border bg-card">
            <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                <KeyRound className="h-3.5 w-3.5 text-primary" />
                <span className="text-xs font-medium">Приглашения на тестирование</span>
                <span className="ml-auto text-[10px] text-muted-foreground">
                    выданы специалистом
                </span>
            </div>
            {invitations.map((invitation) => {
                const link = invitation.active && invitation.token && baseUrl
                    ? pollLink(baseUrl, invitation.token)
                    : '';
                return (
                    <div key={invitation.id} className="border-b px-3 py-2.5 last:border-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">{invitation.methodName}</span>
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] ${
                                invitation.active
                                    ? 'border-primary/30 bg-primary/10 text-primary'
                                    : 'border-border bg-muted/50 text-muted-foreground'
                            }`}>
                                {invitation.active
                                    ? 'действует'
                                    : invitation.expired ? 'срок истёк' : invitation.statusLabel}
                            </span>
                            {invitation.expiresAt && (
                                <span className="text-[10px] text-muted-foreground">
                                    {invitation.expired ? 'истекла' : 'до'} {formatValue(invitation.expiresAt, '', 'timestamp')}
                                </span>
                            )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            {invitation.active && invitation.code && (
                                <span className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono text-xs tracking-wider">
                                    {invitation.code}
                                </span>
                            )}
                            {invitation.active && invitation.code && <CopyButton text={invitation.code} compact />}
                            {link && (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 px-2 text-[11px]"
                                        onClick={() => openExternalUrl(link)}
                                    >
                                        <ExternalLink className="mr-1 h-3 w-3" />
                                        Открыть тест
                                    </Button>
                                    <CopyButton text={link} label="Ссылка" compact />
                                </>
                            )}
                            {!invitation.active && invitation.expired && (
                                <span className="text-[11px] text-muted-foreground">
                                    Срок действия ссылки истёк — попросите специалиста выдать новую.
                                </span>
                            )}
                            {!invitation.active && !invitation.expired && (
                                <span className="text-[11px] text-muted-foreground">
                                    Ссылка/код ещё не выданы — их передаёт специалист.
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
            <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
                Если код не подходит — откройте страницу ввода кода: {' '}
                <button
                    type="button"
                    className="text-primary underline-offset-2 hover:underline"
                    onClick={openCodeEntry}
                >
                    ввести код
                </button>
                .
            </div>
        </div>
    );
}

export function PatientAccessTab({ data, onRefresh }: Props) {
    return (
        <div className="space-y-4">
            <IdentityCard data={data} />
            <AccessRows rows={data.access} onRetry={onRefresh} />
            <Invitations data={data} onRefresh={onRefresh} />
            <div className="rounded-lg border bg-card px-3 py-2 text-[11px] text-muted-foreground">
                Кабинет работает в режиме просмотра медицинских данных. Права на приёмы, ЭКГ и записи
                выдаёт администратор базы; тесты назначает специалист.
            </div>
        </div>
    );
}
